import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, Mail, Key, Building2, GraduationCap, BookOpen, Award, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/training`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_omni-solutions-1/artifacts/nc5fgtrv_Logo.jpg";

const TrainingLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !accessCode) {
      toast.error("Please enter both email and access code");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("access_code", accessCode);

      const response = await axios.post(`${API}/login`, formData);
      
      localStorage.setItem("training_token", response.data.token);
      localStorage.setItem("training_candidate", JSON.stringify(response.data.candidate));
      
      toast.success(`Welcome, ${response.data.candidate.full_name}!`);
      navigate("/training/portal");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid email or access code");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl flex flex-col lg:flex-row items-center gap-12">
        {/* Left Side - Hero Content */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex-1 text-center lg:text-left"
        >
          <div className="mb-6">
            <img src={LOGO_URL} alt="Omnilink Solutions" className="h-14 mx-auto lg:mx-0" />
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Your Learning
            <span className="block text-[#1e3a8a]">Journey Begins</span>
          </h1>
          
          <p className="text-lg text-slate-600 mb-8 max-w-md mx-auto lg:mx-0">
            Complete your 3-day training program to kickstart your career at Omnilink Solutions.
          </p>

          {/* Feature Cards */}
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <BookOpen className="w-5 h-5 text-[#1e3a8a]" />
              </div>
              <p className="text-2xl font-bold text-slate-900">3</p>
              <p className="text-xs text-slate-500">Days</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center"
            >
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">15+</p>
              <p className="text-xs text-slate-500">Modules</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 text-center"
            >
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">24h</p>
              <p className="text-xs text-slate-500">Access</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Side - Login Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a8a] to-[#4f46e5] rounded-xl flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                Training Portal
              </CardTitle>
              <CardDescription className="text-slate-600">
                Login with your credentials provided by HR
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="h-11"
                    data-testid="training-login-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="accessCode" className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-slate-500" />
                    Access Code
                  </Label>
                  <Input
                    id="accessCode"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXX"
                    className="h-11 font-mono tracking-widest text-center"
                    maxLength={6}
                    data-testid="training-login-access-code"
                  />
                  <p className="text-xs text-slate-500">
                    Your 6-character access code was provided by HR
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#1e3a8a] hover:bg-[#172554]"
                  disabled={isLoading}
                  data-testid="training-login-submit"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Logging in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-5 h-5" />
                      Start Training
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-slate-500 mb-2">Having trouble logging in?</p>
                <a 
                  href="mailto:info@omnilinksolutions.net" 
                  className="text-[#1e3a8a] hover:underline text-sm font-medium"
                >
                  Contact HR at info@omnilinksolutions.net
                </a>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <a href="/" className="text-slate-500 hover:text-slate-700 text-sm flex items-center justify-center gap-2">
              <Building2 className="w-4 h-4" />
              Back to Omnilink Solutions Website
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrainingLogin;
